<template>
    <div class="tl-report-container">
        <h6 class="q-mt-md q-mb-none">Generate a draft monthly team-lead report.</h6>
        <p class="text-grey-7 q-mt-sm">
            Select a reporting period and the service(s) you'd like to cover.<br>
            A Word document with AI-drafted responses to each section will download immediately.<br>
            <strong>Ensure you review the automatically-generated responses before finalising the report.</strong>
        </p>

        <div class="row q-gutter-md q-mb-md items-center">
            <q-input
                v-model="startDate"
                label="Period start"
                type="date"
                stack-label
                outlined
                dense
                bg-color="white"
                class="col-3"
            />
            <q-input
                v-model="endDate"
                label="Period end"
                type="date"
                stack-label
                outlined
                dense
                bg-color="white"
                class="col-3"
            />
            <q-select
                v-model="selectedServices"
                :options="serviceOptions"
                :loading="loadingServices"
                label="Service(s)"
                multiple
                use-chips
                clearable
                stack-label
                outlined
                dense
                bg-color="white"
                class="col-4"
            >
            </q-select>
        </div>

        <q-btn
            :loading="generating"
            label="Generate Report"
            color="secondary"
            no-caps
            class="q-mt-md"
            @click="generateReport"
        />
    </div>
</template>

<script>
import notify from '@/services/util/notify';
import api from '@/services/api/api';
import { promptDownload } from '@/services/util/file';

const previousCalendarMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: fmt(start), end: fmt(end) };
};

export default {
    name: 'TeamLeadReport',

    data() {
        const defaults = previousCalendarMonth();
        return {
            startDate: defaults.start,
            endDate: defaults.end,
            selectedServices: [],
            serviceOptions: [],
            loadingServices: false,
            generating: false
        };
    },

    created() {
        this.loadServiceOptions();
    },

    methods: {
        async loadServiceOptions() {
            this.loadingServices = true;
            try {
                const res = await api.client.getServicesList();
                this.serviceOptions = res?.data?.services ?? [];
            } catch (err) {
                console.error(err);
                notify.error('Could not load service list.', 'top');
            } finally {
                this.loadingServices = false;
            }
        },

        async generateReport() {
            if (!this.startDate || !this.endDate) {
                notify.error('Please select a start and end date', 'top');
                return;
            }
            this.generating = true;
            try {
                const services = this.selectedServices.length
                    ? this.selectedServices.join(', ')
                    : undefined;
                const blob = await api.reports.generateTLReport({
                    start: this.startDate,
                    end: this.endDate,
                    services
                });
                const slug = `${this.startDate}_to_${this.endDate}`;
                promptDownload(blob, `TL-Report-${slug}.docx`);
            } catch (err) {
                console.error(err);
                notify.error('There was an issue generating the report.', 'top');
            } finally {
                this.generating = false;
            }
        }
    }
};
</script>

<style scoped>
.tl-report-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: white;
    border-radius: 25px;
    padding: 2rem;
}
</style>
